import React, { useState, useMemo, useCallback, useEffect, useRef, Suspense } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { X, Plus, Minus, Layers, DollarSign, Shield, ArrowRight, Sparkles, ChevronDown, Target, GitCompare, BarChart3, Menu } from 'lucide-react';
import { useAuth } from './contexts/AuthContext';
import { ConversationProvider } from './contexts/ConversationContext';
import SignInPrompt from './components/SignInPrompt';
import StarBorder from './components/StarBorder';

// Data imports
import { taskDefinitions, industryData, categoryIcons, toolData, availableSoftware, userRoles, getLogoBase64 } from './data/toolData';

// Tool Logo component - uses actual logo or falls back to category icon
function ToolLogo({ tool, size = 'md', className = '' }) {
  const logoBase64 = getLogoBase64(tool.name);
  const sizeClasses = {
    xs: 'w-5 h-5 rounded text-[9px]',
    sm: 'w-8 h-8 rounded-lg text-sm',
    md: 'w-10 h-10 rounded-xl text-base',
    lg: 'w-12 h-12 rounded-xl text-xl'
  };
  
  if (logoBase64) {
    return (
      <img 
        src={logoBase64} 
        alt={tool.name}
        className={`${sizeClasses[size]} object-contain ${className}`}
        style={{ backgroundColor: tool.bg }}
      />
    );
  }
  
  // Fallback to category icon
  return (
    <div 
      className={`${sizeClasses[size]} flex items-center justify-center ${className}`}
      style={{ backgroundColor: tool.bg }}
    >
      {categoryIcons[tool.category]}
    </div>
  );
}
import { capabilityProfiles, defaultProfile } from './data/capabilityProfiles';

// Utility imports
import { calculateTaskFitScore, getTaskFitLabel } from './utils/scoring';

// Component imports
import CapabilityBar from './components/CapabilityBar';
import FitScoreBadge from './components/FitScoreBadge';
import ComparisonView from './components/ComparisonView';
import CategoryCompareView from './components/CategoryCompareView';
import Globe from './components/Globe';
import GlassSurface from './components/GlassSurface';
import ShinyText from './components/ShinyText';
import StaggeredProfileMenu from './components/StaggeredProfileMenu';
import StaggeredMenu from './components/StaggeredMenu';
import SpiralLoader from './components/SpiralLoader';
import ConversationOverlay from './components/ConversationOverlay';
import RecommendationsPanel from './components/RecommendationsPanel';
import CategoryFilterBar from './components/CategoryFilterBar';
const WorkflowPage = React.lazy(() => import('./components/WorkflowPage'));
const OnboardingQuiz = React.lazy(() => import('./components/OnboardingQuiz'));
const BlogPage = React.lazy(() => import('./components/BlogPage'));
const DatabasePage = React.lazy(() => import('./components/DatabasePage'));
const ToolDetailPage = React.lazy(() => import('./components/ToolDetailPage'));
// UNDER CONSTRUCTION: PricingCalculator hidden
// import PricingCalculator from './components/PricingCalculator';
const HomePage = React.lazy(() => import('./components/HomePage'));
import Footer from './components/Footer';
import { useAI } from './hooks/useAI';
import SEOHead from './components/SEOHead';
import { HOMEPAGE_ROUTE, EXPLORER_ROUTE } from './data/siteConfig.js';

// Max total points for priorities
const MAX_PRIORITY_POINTS = 100;

// Page definitions with routes
const pages = [
  { id: 'explore', label: 'explore', path: EXPLORER_ROUTE },
  { id: 'workflow', label: 'workflow', path: '/workflow' },
  { id: 'database', label: 'database', path: '/database' },
  { id: 'blog', label: 'blog', path: '/blog' },
];

const TYPEWRITER_PROMPTS = [
  "What AI tools can automate my marketing?",
  "Compare GPT-4 vs Claude for my project...",
  "Build me an AI workflow for content creation",
  "What's the best AI tool for my budget?",
  "Help me find tools that integrate with Slack",
  "Which AI can help my sales team close faster?",
];

function PageLoader({ label = 'Loading page...' }) {
  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-[#030303]/95 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-4">
        <SpiralLoader />
        <p className="text-white/40 text-sm">{label}</p>
      </div>
    </div>
  );
}

function LoginWall() {
  const [promptIndex, setPromptIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [hovering, setHovering] = useState(false);

  useEffect(() => {
    const currentPrompt = TYPEWRITER_PROMPTS[promptIndex];
    let timeout;

    if (!isDeleting && displayText.length < currentPrompt.length) {
      timeout = setTimeout(() => {
        setDisplayText(currentPrompt.slice(0, displayText.length + 1));
      }, 75 + Math.random() * 40);
    } else if (!isDeleting && displayText.length === currentPrompt.length) {
      timeout = setTimeout(() => setIsDeleting(true), 2200);
    } else if (isDeleting && displayText.length > 0) {
      timeout = setTimeout(() => {
        setDisplayText(displayText.slice(0, -1));
      }, 25);
    } else if (isDeleting && displayText.length === 0) {
      setIsDeleting(false);
      setPromptIndex((prev) => (prev + 1) % TYPEWRITER_PROMPTS.length);
    }

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, promptIndex]);

  return (
    <div className="relative">
      {/* Ambient aura glow — subtle pulse */}
      <div className="absolute inset-0 -inset-x-16 -inset-y-12 pointer-events-none">
        <div className="login-aura absolute inset-0 bg-gradient-radial from-blue-500/[0.07] via-transparent to-transparent rounded-full blur-3xl" />
      </div>

      {/* Main bar */}
      <StarBorder
        color={hovering ? 'rgba(59,130,246,0.9)' : 'rgba(59,130,246,0.7)'}
        speed="12s"
        thickness={2}
        className="rounded-2xl"
      >
      <Link
        to="/onboarding"
        className="group relative flex items-center w-full h-16 md:h-20 px-4 md:px-7 rounded-2xl bg-[#09090b] backdrop-blur-2xl transition-all duration-300"
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        {/* Sparkle icon */}
        <Sparkles className="w-5 h-5 md:w-7 md:h-7 mr-3 md:mr-5 flex-shrink-0 text-blue-400" />

        {/* Typewriter text */}
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-center">
            <span className="text-white/80 text-sm md:text-xl font-light truncate">{displayText}</span>
            <span className="typewriter-cursor text-blue-400 text-sm md:text-xl font-light ml-0.5">|</span>
          </div>
          <span className="text-zinc-600 text-xs md:text-sm mt-0.5 block">Create your free profile to unlock AI chat</span>
        </div>

        {/* CTA section */}
        <div className="flex items-center ml-2 md:ml-4 flex-shrink-0">
          <div className={`login-cta-pill relative px-3 py-2 md:px-5 md:py-2.5 rounded-full transition-all duration-500 backdrop-blur-md ${
            hovering
              ? 'bg-amber-400/[0.06] shadow-lg shadow-amber-400/10'
              : 'bg-white/[0.02]'
          }`}>
            <span className={`text-xs md:text-sm font-medium tracking-wide flex items-center gap-1.5 md:gap-2 whitespace-nowrap transition-colors duration-500 ${
              hovering ? 'text-white' : 'text-white'
            }`}>
              <span className="relative">
                <span className="hidden md:inline">Unlock Touchstone Guide</span>
                <span className="md:hidden">Unlock</span>
                <span className={`absolute bottom-0 left-0 h-px bg-amber-400/60 transition-all duration-500 ease-out ${
                  hovering ? 'w-full' : 'w-0'
                }`} />
              </span>
              <ArrowRight className={`w-3.5 h-3.5 md:w-4 md:h-4 transition-all duration-500 ${
                hovering ? 'translate-x-1 text-amber-400' : ''
              }`} />
            </span>
          </div>
        </div>

      </Link>
      </StarBorder>

    </div>
  );
}

export default function AIToolExplorer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSignedIn, user, profile: authProfile, isOnboarded, saveProfile } = useAuth();
  
  // Derive current page from URL path
  const currentPage = useMemo(() => {
    const path = location.pathname;
    if (path === '/home') return 'home';
    if (path === '/onboarding') return 'onboarding';
    if (path === '/' || path === '') return 'explore';
    if (path.startsWith('/workflow')) return 'workflow';
    if (path.startsWith('/database')) return 'database';
    if (path.startsWith('/blog')) return 'blog';
    return 'explore';
  }, [location.pathname]);
  
  const [isExploreLoading, setIsExploreLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Handle page change with navigation
  const handlePageChange = useCallback((pageId) => {
    const page = pages.find(p => p.id === pageId);
    if (page) {
      if (pageId === 'explore' && currentPage !== 'explore') {
        setIsExploreLoading(true);
      }
      navigate(page.path);
    }
  }, [currentPage, navigate]);
  
  // Handle incoming tool selection from blog posts
  useEffect(() => {
    if (location.state?.selectToolId) {
      const tool = toolData.find(t => t.id === location.state.selectToolId);
      if (tool) {
        setSelectedTool(tool);
        setIsExploringMode(true);
      }
      // Clear the state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);
  
  // Handle Globe ready callback
  const handleGlobeReady = useCallback(() => {
    setIsExploreLoading(false);
  }, []);
  
  // Mark initial load as complete
  useEffect(() => {
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isInitialLoad]);

  
  const [activeTask, setActiveTask] = useState(null);
  const [activeIndustry, setActiveIndustry] = useState(null);
  const [activeGoal, setActiveGoal] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);
  const [currentStack, setCurrentStack] = useState([]);
  const [hoveredTool, setHoveredTool] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [zoomLevel, setZoomLevel] = useState(0.65); // Start zoomed out
  const zoomRef = useRef(0.65);
  const [compareTarget, setCompareTarget] = useState(null);
  const [showCompareMenu, setShowCompareMenu] = useState(false);
  const [showCategoryCompare, setShowCategoryCompare] = useState(false);
  const [isExploringMode, setIsExploringMode] = useState(false);
  const [userSoftware, setUserSoftware] = useState([]);
  const [showSoftwareSelector, setShowSoftwareSelector] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Role preferences
  const [userRole, setUserRole] = useState(null);
  const [showRoleSelector, setShowRoleSelector] = useState(false);
  
  // Combined Profile Modal
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileTab, setProfileTab] = useState('overview');
  
  // Pricing preferences - with priority allocation (total must be <= 100)
  const [pricingPrefs, setPricingPrefs] = useState({
    monthlyBudget: '',
    priorities: {
      cost: 20,
      quality: 20,
      speed: 20,
      easeOfUse: 20,
      support: 20,
    },
    requireFreeTier: false,
    preferOpenSource: false,
    requireSelfHost: false,
    requireSOC2: false,
    requireHIPAA: false,
  });
  const [showPricingSelector, setShowPricingSelector] = useState(false);

  // Onboarding: profile from AuthContext (Firestore + localStorage)
  const savedProfileRef = useRef(null);

  const checkOnboarded = useCallback(() => {
    return isOnboarded && isSignedIn;
  }, [isSignedIn, isOnboarded]);
  
  // Hydrate app state when profile loads (from Firestore or localStorage cache)
  useEffect(() => {
    if (authProfile) {
      savedProfileRef.current = authProfile;
      if (authProfile.role) setUserRole(authProfile.role);
      if (authProfile.software?.length) setUserSoftware(authProfile.software);
      if (authProfile.priorities) {
        setPricingPrefs(prev => ({
          ...prev,
          priorities: authProfile.priorities,
          monthlyBudget: authProfile.budget || prev.monthlyBudget,
          requireFreeTier: authProfile.requirements?.requireFreeTier || false,
          preferOpenSource: authProfile.requirements?.preferOpenSource || false,
          requireSelfHost: authProfile.requirements?.requireSelfHost || false,
          requireSOC2: authProfile.requirements?.requireSOC2 || false,
          requireHIPAA: authProfile.requirements?.requireHIPAA || false,
        }));
      }
    }
  }, [authProfile]);
  
  // Close mobile menu on route change
  useEffect(() => { setMobileMenuOpen(false); }, [currentPage]);

  // State for fit score detail modal
  const [showFitDetails, setShowFitDetails] = useState(false);
  
  // AI-powered search
  const { semanticSearch, isOnline: isAIOnline } = useAI();
  const [aiSearchResults, setAiSearchResults] = useState([]);
  const [isAISearching, setIsAISearching] = useState(false);
  const searchTimeoutRef = useRef(null);
  
  // Conversational AI Guide
  const [showConversation, setShowConversation] = useState(false);
  const [conversationInitialMessage, setConversationInitialMessage] = useState(null);
  const [highlightedToolNames, setHighlightedToolNames] = useState([]);
  const [dimmedToolNames, setDimmedToolNames] = useState([]);
  const [hasConversationMessages, setHasConversationMessages] = useState(false);
  const [showRecommendationsPanel, setShowRecommendationsPanel] = useState(true);
  const [spotlightTool, setSpotlightTool] = useState(null);
  const chatContainerRef = useRef(null);

  // Determine spotlight mode when single recommendation
  useEffect(() => {
    if (highlightedToolNames.length === 1) {
      const tool = toolData.find(t => t.name.toLowerCase() === highlightedToolNames[0].toLowerCase());
      setSpotlightTool(tool || null);
    } else {
      setSpotlightTool(null);
    }
  }, [highlightedToolNames]);

  // Listen for conversation reopen from collapsed preview
  useEffect(() => {
    const handleReopen = () => {
      setShowConversation(true);
      setIsExploringMode(true);
    };
    
    window.addEventListener('conversation-reopen', handleReopen);
    return () => window.removeEventListener('conversation-reopen', handleReopen);
  }, []);

  // Close chat when clicking outside
  useEffect(() => {
    if (!showConversation) return;
    
    const handleClickOutside = (e) => {
      if (chatContainerRef.current && !chatContainerRef.current.contains(e.target)) {
        setShowConversation(false);
        setHighlightedToolNames([]);
        setDimmedToolNames([]);
        setSearchQuery('');
      }
    };
    
    // Add listener with a small delay to avoid immediate trigger
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showConversation]);

  // Mouse wheel zoom handler - only active on explore page
  useEffect(() => {
    const handleWheel = (e) => {
      // Only zoom when on explore page
      if (currentPage !== 'explore') return;
      
      // Only zoom when hovering over globe area (not UI panels)
      const target = e.target;
      const isOverUI = target.closest('[data-ui-panel]') || 
                       target.closest('button') || 
                       target.closest('input') ||
                       target.closest('[role="dialog"]');
      
      if (isOverUI) return;
      
      e.preventDefault();
      
      // Zoom speed factor
      const zoomSpeed = 0.001;
      const delta = -e.deltaY * zoomSpeed;
      
      setZoomLevel(prev => {
        const newZoom = prev + delta;
        // Clamp between 0.5 (zoomed out) and 1.3 (max zoom - can't go inside planet)
        return Math.max(0.5, Math.min(1.3, newZoom));
      });
    };
    
    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => window.removeEventListener('wheel', handleWheel);
  }, [currentPage]);

  // Determine if we're in "exploring" mode
  const isExploring = activeIndustry !== null || activeGoal !== null || isExploringMode;

  // Keep ref in sync with state
  useEffect(() => {
    zoomRef.current = zoomLevel;
  }, [zoomLevel]);

  // Zoom in when entering exploring mode
  useEffect(() => {
    if (isExploring) {
      // Animate zoom from current to 1.25 (closer view)
      const startZoom = zoomRef.current;
      const targetZoom = 1.25;
      const duration = 1400; // ms - slower, smoother
      const startTime = Date.now();
      
      const animateZoom = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // Ease in-out quart for ultra smooth transition
        const eased = progress < 0.5
          ? 8 * progress * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 4) / 2;
        const newZoom = startZoom + (targetZoom - startZoom) * eased;
        
        setZoomLevel(newZoom);
        
        if (progress < 1) {
          requestAnimationFrame(animateZoom);
        }
      };
      
      animateZoom();
    }
  }, [isExploring]); // Only trigger when isExploring changes

  const inStack = useCallback((id) => currentStack.some(t => t.id === id), [currentStack]);
  const taskDef = activeTask ? taskDefinitions[activeTask] : null;
  
  // Build filters object from preferences
  const filters = useMemo(() => ({
    ...pricingPrefs,
    role: userRole,
  }), [pricingPrefs, userRole]);

  const rankedTools = useMemo(() => {
    if (!activeTask) return toolData.map(t => ({ ...t, fitScore: 50, fitBreakdown: null }));
    return toolData.map(t => {
      const result = calculateTaskFitScore(t, activeTask, filters, userSoftware);
      return { ...t, fitScore: result.score, fitBreakdown: result.breakdown };
    }).sort((a, b) => b.fitScore - a.fitScore);
  }, [activeTask, filters, userSoftware]);

  // Keyword search filtered tools
  const keywordResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase().trim();
    return rankedTools.filter(tool => 
      tool.name.toLowerCase().includes(query) ||
      tool.category.toLowerCase().includes(query) ||
      tool.provider?.toLowerCase().includes(query) ||
      tool.guidance?.toLowerCase().includes(query) ||
      tool.software?.some(s => s.toLowerCase().includes(query))
    ).slice(0, 8);
  }, [searchQuery, rankedTools]);

  // Debounced AI search
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.length < 3) {
      setAiSearchResults([]);
      return;
    }
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce AI search by 500ms
    searchTimeoutRef.current = setTimeout(async () => {
      setIsAISearching(true);
      try {
        const toolNames = await semanticSearch(searchQuery, {
          userRole,
          activeTask,
          userSoftware,
        });
        // Convert tool names to tool objects
        const tools = toolNames
          .map(name => rankedTools.find(t => t.name.toLowerCase() === name.toLowerCase()))
          .filter(Boolean);
        setAiSearchResults(tools);
      } catch (err) {
        console.error('AI search failed:', err);
      } finally {
        setIsAISearching(false);
      }
    }, 500);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, semanticSearch, userRole, activeTask, userSoftware, rankedTools]);

  // Combined search results - AI results first, then keyword results (deduplicated)
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    
    // If AI results available, prioritize them
    if (aiSearchResults.length > 0) {
      const aiIds = new Set(aiSearchResults.map(t => t.id));
      const additionalKeyword = keywordResults.filter(t => !aiIds.has(t.id));
      return [...aiSearchResults, ...additionalKeyword].slice(0, 8);
    }
    
    return keywordResults;
  }, [searchQuery, aiSearchResults, keywordResults]);

  const topTools = useMemo(() => {
    if (!activeTask) return [];
    const taskDefinition = taskDefinitions[activeTask];
    if (!taskDefinition) return [];
    return rankedTools.filter(t => taskDefinition.categories.includes(t.category)).slice(0, 4);
  }, [activeTask, rankedTools]);

  // Select task but stay in preview mode
  const handleTaskSelect = useCallback((taskKey) => {
    if (activeTask === taskKey) {
      setActiveTask(null);
    } else {
      setActiveTask(taskKey);
    }
    setSelectedTool(null);
    setCompareTarget(null);
    setShowCompareMenu(false);
    setShowCategoryCompare(false);
    setShowFitDetails(false);
  }, [activeTask]);

  // Goal button action — build a profile-aware prompt and open AI chat
  const handleGoalAction = useCallback((category) => {
    if (!isSignedIn) {
      navigate('/onboarding');
      return;
    }

    const profile = authProfile || savedProfileRef.current;
    const parts = [];

    if (profile?.role) {
      const roleObj = userRoles.find(r => r.id === profile.role);
      parts.push(`I'm a ${roleObj?.name || profile.role}`);
    }

    if (profile?.software?.length) {
      const stackNames = profile.software.slice(0, 5).join(', ');
      parts.push(`currently using ${stackNames}`);
    }

    if (profile?.budget) {
      parts.push(`with a ${profile.budget} monthly budget`);
    }

    const intro = parts.length > 0
      ? `${parts.join(', ')}. `
      : '';

    const prompt = `${intro}I want to ${category.promptIntent}. What AI tools should I use and how should I set up my workflow?`;

    setActiveGoal(category.id);
    setConversationInitialMessage(prompt);
    setShowConversation(true);
    setIsExploringMode(true);
  }, [isSignedIn, authProfile, navigate]);

  // Clear everything and go back
  const clearAll = useCallback(() => {
    setActiveIndustry(null);
    setActiveGoal(null);
    setActiveTask(null);
    setSelectedTool(null);
    setIsExploringMode(false);
    setShowFitDetails(false);
  }, []);

  const toggleStack = useCallback((tool) => {
    setCurrentStack(prev => {
      if (prev.some(t => t.id === tool.id)) return prev.filter(t => t.id !== tool.id);
      if (prev.length >= 8) return prev;
      return [...prev, tool];
    });
  }, []);

  // Normalize angle to be between -PI and PI
  const normalizeAngle = (angle) => {
    while (angle > Math.PI) angle -= Math.PI * 2;
    while (angle < -Math.PI) angle += Math.PI * 2;
    return angle;
  };

  // Close fit details when selecting a different tool
  useEffect(() => {
    setShowFitDetails(false);
  }, [selectedTool]);

  // PRE-CALCULATE visual state for each tool (only recalculates when deps change, NOT every frame)
  const visualState = useMemo(() => {
    const taskCategories = taskDef ? taskDef.categories : [];
    const industryCategories = activeIndustry ? industryData[activeIndustry]?.relevantCategories || [] : [];
    const rankedMap = new Map();
    rankedTools.forEach(t => rankedMap.set(t.id, t));
    const stackSet = new Set(currentStack.map(t => t.id));
    
    return toolData.map(tool => {
      const toolRanked = rankedMap.get(tool.id);
      const fitScore = toolRanked?.fitScore || 0;
      const isHighFit = activeTask && fitScore >= 70;
      const isMedFit = activeTask && fitScore >= 50;
      const isTaskRelevant = taskCategories.includes(tool.category);
      const isIndustryRelevant = industryCategories.includes(tool.category);
      const isStk = stackSet.has(tool.id);
      
      let baseAlpha = 0.5;
      let sizeMultiplier = 1;
      
      if (!activeTask && !activeIndustry) {
        baseAlpha = 0.45;
      } else if (activeTask) {
        if (isHighFit) { baseAlpha = 1; sizeMultiplier = 1.15; }
        else if (isMedFit) baseAlpha = 0.7;
        else if (isTaskRelevant) baseAlpha = 0.35;
        else baseAlpha = 0.12;
      } else if (activeIndustry) {
        if (isIndustryRelevant) baseAlpha = 0.8;
        else baseAlpha = 0.12;
      }
      
      if (isStk) baseAlpha = Math.max(0.95, baseAlpha);
      
      return {
        id: tool.id,
        bg: tool.bg,
        category: tool.category,
        baseAlpha,
        sizeMultiplier,
        isHighFit,
        isStk
      };
    });
  }, [activeTask, activeIndustry, rankedTools, currentStack, taskDef]);

  // Build visual lookup map
  const visualMap = useMemo(() => {
    const map = new Map();
    visualState.forEach(v => map.set(v.id, v));
    return map;
  }, [visualState]);

  // Handle tool click from Globe
  const handleToolClick = useCallback((tool) => {
    if (selectedTool?.id === tool.id) {
      setSelectedTool(null);
    } else {
      setSelectedTool(tool);
      setIsExploringMode(true);
      setCompareTarget(null);
      setShowCompareMenu(false);
      setShowCategoryCompare(false);
    }
  }, [selectedTool]);

  const selectedFitResult = selectedTool && activeTask ? calculateTaskFitScore(selectedTool, activeTask, filters, userSoftware) : null;
  const selectedFitScore = selectedFitResult?.score || null;
  const selectedFitBreakdown = selectedFitResult?.breakdown || null;
  const selectedFitLabel = selectedFitScore ? getTaskFitLabel(selectedFitScore) : null;
  const profile = selectedTool ? (capabilityProfiles[selectedTool.id] || defaultProfile) : null;

  return (
    <ConversationProvider>
    <div className="relative w-screen overflow-hidden bg-[#030303] text-white font-sans antialiased" style={{ height: '100dvh' }}>
      {currentPage === 'explore' && (
        <SEOHead
          title="AI Tool Globe Explorer"
          description="Interactive globe for exploring AI tools and categories inside Touchstone. For extractable research content, use the homepage, database, and blog."
          path="/"
          dateModified="2026-03-23"
          noindex={true}
        />
      )}
      
      {/* Metallic Background Effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050508] via-[#08080a] to-[#040406]" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-radial from-zinc-800/15 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDuration: '8s' }} />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-radial from-zinc-700/10 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDuration: '12s', animationDelay: '2s' }} />
          <div className="absolute top-1/3 right-1/3 w-64 h-64 bg-gradient-radial from-blue-950/8 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDuration: '10s', animationDelay: '4s' }} />
        </div>
        {/* Metallic noise overlay */}
        <div className="absolute inset-0 opacity-[0.01]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }} />
      </div>
      
      {/* Glass Header — one header for all pages */}
      <div className={`absolute top-3 left-3 right-3 md:top-4 md:left-4 md:right-4 z-[60] ${isDragging ? 'pointer-events-none' : ''}`}>
        <GlassSurface
          height={isMobile ? 56 : 96}
          borderRadius={isMobile ? 14 : 16}
          brightness={40}
          opacity={0.9}
          className="w-full"
          style={{ width: '100%' }}
        >
          <div className="flex items-center justify-between w-full h-full px-4 md:px-6">
            <div className="flex items-center min-w-0">
              <Link to={HOMEPAGE_ROUTE} className="hover:opacity-80 transition-opacity flex-shrink-0">
                <div
                  style={{
                    fontFamily: "'Qulio Display', 'Bodoni Moda', serif",
                    display: 'inline-block',
                    letterSpacing: '0.05em',
                    ...(currentPage === 'home' ? {
                      textShadow: '0 0 6px rgba(192, 192, 192, 0.5), 0 0 12px rgba(192, 192, 192, 0.35), 0 0 18px rgba(192, 192, 192, 0.2)',
                      filter: 'drop-shadow(0 0 4px rgba(192, 192, 192, 0.5)) drop-shadow(0 0 8px rgba(192, 192, 192, 0.3))'
                    } : {}),
                  }}
                >
                  <span className="text-[28px] md:text-4xl text-white">TOUCHSTONE</span>
                </div>
              </Link>
              {/* Mobile: show current page label */}
              {(() => {
                const activeLabel = pages.find(p => p.id === currentPage)?.label || (currentPage === 'home' ? 'home' : currentPage === 'onboarding' ? 'onboarding' : null);
                return activeLabel ? (
                  <span
                    className="md:hidden flex items-center ml-2 text-[13px] font-medium text-white uppercase"
                    style={{ fontFamily: "'Qulio Display', 'Bodoni Moda', serif", letterSpacing: '0.08em' }}
                  >
                    <span className="mx-2 text-white/15">/</span>
                    {activeLabel}
                  </span>
                ) : null;
              })()}
              {/* Desktop nav links */}
              <div className="hidden md:flex items-center ml-3">
                {pages.map((page, index) => (
                  <Link
                    key={page.id}
                    to={page.path}
                    onClick={() => {
                      if (page.id === 'explore' && currentPage !== 'explore') {
                        setIsExploreLoading(true);
                      }
                    }}
                    className={`flex items-center hover:opacity-80 transition-opacity ${index > 0 ? 'ml-4' : ''}`}
                  >
                    <div
                      className={`w-2.5 h-2.5 border-r-2 border-b-2 rotate-[-45deg] ${
                        currentPage === page.id ? 'border-white' : 'border-white/40'
                      }`}
                      style={{ marginRight: '8px' }}
                    />
                    {currentPage === page.id ? (
                      <span
                        className="text-lg font-medium text-white"
                        style={{
                          fontFamily: "'Qulio Display', 'Bodoni Moda', serif",
                          letterSpacing: '0.05em',
                          textShadow: '0 0 6px rgba(192, 192, 192, 0.5), 0 0 12px rgba(192, 192, 192, 0.35), 0 0 18px rgba(192, 192, 192, 0.2)',
                          filter: 'drop-shadow(0 0 4px rgba(192, 192, 192, 0.5)) drop-shadow(0 0 8px rgba(192, 192, 192, 0.3))'
                        }}
                      >
                        {page.label}
                      </span>
                    ) : (
                      <span className="text-lg font-medium text-white/40" style={{ fontFamily: "'Qulio Display', 'Bodoni Moda', serif", letterSpacing: '0.05em' }}>
                        {page.label}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
              
            </div>
            <div className="flex items-center gap-2">
              {currentStack.length > 0 && (
                <button className="hidden md:flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full transition-all">
                  <Layers className="w-4 h-4 text-white/50" />
                  <span className="text-sm text-white/70">{currentStack.length}</span>
                </button>
              )}
              <button
                data-profile-trigger
                onClick={() => setShowProfileModal(!showProfileModal)}
                className={`hidden md:flex items-center gap-2 px-4 py-2 border rounded-full transition-all ${
                  userRole || userSoftware.length > 0 || pricingPrefs.monthlyBudget !== '' || Object.values(pricingPrefs.priorities).some(v => v !== 20)
                    ? 'bg-gradient-to-r from-blue-500/20 to-amber-400/20 border-blue-500/30'
                    : 'bg-white/5 hover:bg-white/10 border-white/10'
                }`}
              >
                <ShinyText
                  text="Profile"
                  speed={3.7}
                  className="text-sm font-medium"
                  color="#ffffff"
                  shineColor="#f5c542"
                />
                {(userRole || userSoftware.length > 0) && (
                  <span className="w-2 h-2 bg-amber-400 rounded-full" />
                )}
              </button>
              
            </div>
          </div>
        </GlassSurface>
      </div>

      {/* Mobile StaggeredMenu — fullscreen animated nav */}
      <StaggeredMenu
        isOpen={mobileMenuOpen}
        onOpen={() => setMobileMenuOpen(true)}
        onClose={() => setMobileMenuOpen(false)}
        items={[{ label: 'home', path: '/home', ariaLabel: 'Go to home' }, ...pages.map(p => ({ label: p.label, path: p.path, ariaLabel: `Go to ${p.label}` }))]}
        colors={['#0a0a0c', '#0e0e11']}
        accentColor="#14b8a6"
        displayItemNumbering={true}
      >
        <button
          onClick={() => {
            setMobileMenuOpen(false);
            setShowProfileModal(true);
          }}
          className={`w-full flex items-center justify-center gap-2 px-4 py-3 border rounded-xl transition-all ${
            userRole || userSoftware.length > 0
              ? 'bg-gradient-to-r from-blue-500/20 to-amber-400/20 border-blue-500/30'
              : 'bg-white/5 hover:bg-white/10 border-white/10'
          }`}
        >
          <ShinyText text="Profile" speed={3.7} className="text-sm font-medium" color="#ffffff" shineColor="#f5c542" />
          {(userRole || userSoftware.length > 0) && (
            <span className="w-2 h-2 bg-amber-400 rounded-full" />
          )}
        </button>
        {currentStack.length > 0 && (
          <div className="flex items-center justify-center gap-2 mt-3 px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl">
            <Layers className="w-4 h-4 text-white/50" />
            <span className="text-sm text-white/70">{currentStack.length} in stack</span>
          </div>
        )}
      </StaggeredMenu>

      {/* Page Content */}
      {currentPage === 'explore' ? (
        <>
          {/* React Three Fiber Globe - Always render */}
          <Globe
            tools={toolData}
            selectedTool={selectedTool}
            hoveredTool={hoveredTool}
            onHover={setHoveredTool}
            onClick={handleToolClick}
            zoomLevel={zoomLevel}
            onZoomChange={(delta) => setZoomLevel(z => Math.min(Math.max(z + delta, 0.5), 1.3))}
            className="absolute inset-0 w-full h-full"
            onReady={handleGlobeReady}
            highlightedToolNames={highlightedToolNames}
            dimmedToolNames={dimmedToolNames}
            spotlightTool={spotlightTool}
          />

          {/* Recommendations Panel - appears when AI has suggestions */}
          <RecommendationsPanel
            recommendations={highlightedToolNames}
            toolData={toolData}
            onAddToStack={toggleStack}
            onSelectTool={setSelectedTool}
            onClose={() => setShowRecommendationsPanel(false)}
            isVisible={showRecommendationsPanel && highlightedToolNames.length > 0 && showConversation}
          />

          {/* Loading Overlay - Shows on top while loading */}
          {isExploreLoading && !isInitialLoad && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-[#030303]/95 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-4">
                <SpiralLoader />
                <p className="text-white/40 text-sm">Loading explore page...</p>
              </div>
            </div>
          )}

      {/* Role Selector Modal */}
      {showRoleSelector && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-8">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowRoleSelector(false)} />
          <div className="relative bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <button onClick={() => setShowRoleSelector(false)} className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-300 p-1.5 hover:bg-zinc-800 rounded-lg transition-colors z-10">
              <X className="w-5 h-5" />
            </button>
            
            <div className="p-6">
              <div className="text-lg text-white font-medium mb-1">Your Role</div>
              <div className="text-sm text-zinc-500 mb-5">Select your role to prioritize relevant capabilities</div>
              
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {userRoles.map(role => (
                  <button
                    key={role.id}
                    onClick={() => setUserRole(userRole === role.id ? null : role.id)}
                    className={`gradient-role-card w-full flex items-center gap-3 p-3 rounded-xl text-left ${
                      userRole === role.id ? 'selected' : ''
                    }`}
                  >
                    <div className="flex-1">
                      <div className={`text-sm font-medium ${userRole === role.id ? 'text-violet-300' : 'text-zinc-200'}`}>
                        {role.name}
                      </div>
                      <div className="text-xs text-zinc-500">{role.description}</div>
                    </div>
                    {userRole === role.id && <span className="text-violet-400">✓</span>}
                  </button>
                ))}
              </div>
              
              <div className="mt-5 pt-4 border-t border-zinc-800 flex justify-end">
                <button onClick={() => setShowRoleSelector(false)} className="px-4 py-2 bg-violet-500 hover:bg-violet-600 text-white text-sm font-medium rounded-lg transition-colors">
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Selector Modal */}
      {showPricingSelector && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowPricingSelector(false)} />
          <div className="relative bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl w-full max-w-xl">
            <button onClick={() => setShowPricingSelector(false)} className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-300 p-1.5 hover:bg-zinc-800 rounded-lg transition-colors z-10">
              <X className="w-5 h-5" />
            </button>
            
            <div className="p-6">
              <div className="mb-6">
                <div className="text-lg text-white font-medium">Priorities & Budget</div>
                <div className="text-sm text-zinc-500">Customize recommendations to match your needs</div>
              </div>
              
              <div className="mb-6">
                <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Suggested Monthly Budget</div>
                <div className="flex items-center gap-3">
                  <div className="relative w-44">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                      type="text"
                      placeholder="No limit"
                      value={pricingPrefs.monthlyBudget}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || val === '-' || !isNaN(parseFloat(val))) {
                          setPricingPrefs(p => ({ ...p, monthlyBudget: val }));
                        }
                      }}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-emerald-500/50"
                    />
                  </div>
                  <span className="text-sm text-zinc-500">per month total</span>
                </div>
              </div>
              
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs text-zinc-500 uppercase tracking-wider">Priority Allocation</div>
                  <div className={`text-sm px-2.5 py-1 rounded-lg ${
                    Object.values(pricingPrefs.priorities).reduce((a, b) => a + b, 0) > MAX_PRIORITY_POINTS
                      ? 'bg-red-500/20 text-red-400'
                      : 'bg-zinc-800 text-zinc-400'
                  }`}>
                    {Object.values(pricingPrefs.priorities).reduce((a, b) => a + b, 0)} / {MAX_PRIORITY_POINTS} points
                  </div>
                </div>
                
                <div className="space-y-3">
                  {[
                    { key: 'cost', label: 'Cost Savings', icon: '💰', color: '#10b981' },
                    { key: 'quality', label: 'Output Quality', icon: '✨', color: '#8b5cf6' },
                    { key: 'speed', label: 'Speed', icon: '⚡', color: '#f59e0b' },
                    { key: 'easeOfUse', label: 'Ease of Use', icon: '🎯', color: '#14b8a6' },
                    { key: 'support', label: 'Support & Security', icon: '🛡️', color: '#3b82f6' },
                  ].map(priority => {
                    const value = pricingPrefs.priorities[priority.key];
                    const totalOthers = Object.entries(pricingPrefs.priorities)
                      .filter(([k]) => k !== priority.key)
                      .reduce((sum, [, v]) => sum + v, 0);
                    const maxAllowed = Math.min(100, MAX_PRIORITY_POINTS - totalOthers);
                    
                    return (
                      <div key={priority.key} className="flex items-center gap-4">
                        <div className="flex items-center gap-2 w-40">
                          <span className="text-lg">{priority.icon}</span>
                          <span className="text-sm text-zinc-300">{priority.label}</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max={maxAllowed}
                          value={Math.min(value, maxAllowed)}
                          onChange={(e) => setPricingPrefs(p => ({
                            ...p,
                            priorities: { ...p.priorities, [priority.key]: parseInt(e.target.value) }
                          }))}
                          className="flex-1 h-2 bg-zinc-700 rounded-full appearance-none cursor-pointer"
                          style={{ accentColor: priority.color }}
                        />
                        <span className={`text-sm font-medium w-8 text-right ${value > 25 ? 'text-white' : 'text-zinc-600'}`}>{value}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="mb-6">
                <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Quick Presets</div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: 'Balanced', p: { cost: 20, quality: 20, speed: 20, easeOfUse: 20, support: 20 } },
                    { name: 'Budget Focused', p: { cost: 50, quality: 15, speed: 15, easeOfUse: 15, support: 5 } },
                    { name: 'Quality First', p: { cost: 5, quality: 50, speed: 15, easeOfUse: 15, support: 15 } },
                    { name: 'Fast & Easy', p: { cost: 10, quality: 15, speed: 40, easeOfUse: 30, support: 5 } },
                    { name: 'Enterprise', p: { cost: 5, quality: 25, speed: 15, easeOfUse: 15, support: 40 } },
                  ].map(preset => (
                    <button
                      key={preset.name}
                      onClick={() => setPricingPrefs(p => ({ ...p, priorities: preset.p }))}
                      className="px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200 transition-all"
                    >
                      {preset.name}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="mb-6">
                <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Requirements</div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: 'requireFreeTier', label: 'Free Tier' },
                    { key: 'preferOpenSource', label: 'Open Source' },
                    { key: 'requireSelfHost', label: 'Self-Hostable' },
                    { key: 'requireSOC2', label: 'SOC2' },
                    { key: 'requireHIPAA', label: 'HIPAA' },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => setPricingPrefs(p => ({ ...p, [opt.key]: !p[opt.key] }))}
                      className={`px-3 py-2 rounded-lg text-sm transition-all ${
                        pricingPrefs[opt.key] 
                          ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-300' 
                          : 'bg-zinc-800 border border-zinc-700 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
                <button 
                  onClick={() => setPricingPrefs({
                    monthlyBudget: '',
                    priorities: { cost: 20, quality: 20, speed: 20, easeOfUse: 20, support: 20 },
                    requireFreeTier: false,
                    preferOpenSource: false,
                    requireSelfHost: false,
                    requireSOC2: false,
                    requireHIPAA: false,
                  })}
                  className="text-sm text-zinc-500 hover:text-zinc-300"
                >
                  Reset all
                </button>
                <button onClick={() => setShowPricingSelector(false)} className="px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors">
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Software Selector Modal */}
      {showSoftwareSelector && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-8">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowSoftwareSelector(false)} />
          <div className="relative bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden">
            <button onClick={() => setShowSoftwareSelector(false)} className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-300 p-1.5 hover:bg-zinc-800 rounded-lg transition-colors z-10">
              <X className="w-5 h-5" />
            </button>
            
            <div className="p-6">
              <div className="text-lg text-white font-medium mb-1">Your Software Stack</div>
              <div className="text-sm text-zinc-500 mb-5">Select the tools you already use</div>
              
              <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                {['Communication', 'Productivity', 'CRM', 'Design', 'Development', 'Cloud', 'Project Management', 'Automation', 'Observability'].map(category => {
                  const softwareInCategory = availableSoftware.filter(s => s.category === category);
                  if (softwareInCategory.length === 0) return null;
                  
                  return (
                    <div key={category}>
                      <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">{category}</div>
                      <div className="flex flex-wrap gap-2">
                        {softwareInCategory.map(sw => {
                          const isSelected = userSoftware.includes(sw.id);
                          return (
                            <button
                              key={sw.id}
                              onClick={() => {
                                if (isSelected) {
                                  setUserSoftware(prev => prev.filter(id => id !== sw.id));
                                } else {
                                  setUserSoftware(prev => [...prev, sw.id]);
                                }
                              }}
                              className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                                isSelected 
                                  ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-400' 
                                  : 'bg-zinc-800/50 border border-zinc-700/50 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-300'
                              }`}
                            >
                              {sw.name}
                              {isSelected && <span className="ml-1.5">✓</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-5 pt-4 border-t border-zinc-800 flex items-center justify-between">
                <div className="text-sm text-zinc-500">{userSoftware.length} software selected</div>
                <button onClick={() => setShowSoftwareSelector(false)} className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors">
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search + Navigation */}
      <div 
        className={`absolute left-1/2 top-0 z-40 w-full ${isDragging ? 'pointer-events-none' : ''}`}
        style={{
          transform: isExploring 
            ? 'translate(-50%, 176px)' 
            : 'translate(-50%, calc(50vh - 50%))',
          transition: 'transform 0.8s cubic-bezier(0.33, 1, 0.68, 1)',
          willChange: 'transform',
          maxWidth: isExploring ? '64rem' : '64rem',
          padding: isExploring ? '0 1rem' : '0 1rem',
        }}
      >
        <div className="flex flex-col items-center">
          
          {!isExploring && (
            <div className="mb-6 md:mb-10 text-center px-2">
              {/* Mobile: TOUCHSTONE + subtitle */}
              <h1 className="md:hidden text-5xl text-white tracking-tight mb-2" style={{ fontFamily: "'Qulio Display', 'Bodoni Moda', serif", letterSpacing: '0.05em', fontWeight: 400 }}>TOUCHSTONE</h1>
              <p className="md:hidden text-lg text-teal-400 font-medium mb-0" style={{ fontFamily: "'Qulio Display', 'Bodoni Moda', serif", letterSpacing: '0.04em' }}>Navigate the AI landscape</p>
              {/* Desktop: original heading + subtitle */}
              <h1 className="hidden md:block text-6xl text-white tracking-tight mb-4" style={{ fontFamily: "'Qulio Display', 'Bodoni Moda', serif", letterSpacing: '0.05em', fontWeight: 400 }}>Navigate the AI landscape</h1>
              <p className="hidden md:block text-xl text-blue-300 font-light">Discover AI tools that fit how you work — Compare, choose, integrate</p>
            </div>
          )}

          <div ref={chatContainerRef} className="relative w-full max-w-4xl z-50">
            {!checkOnboarded() ? (
              /* Premium signup CTA — animated typewriter bar with floating chips */
              <LoginWall />
            ) : (
              /* Normal chat bar for signed-up users */
              <>
                <StarBorder
                  color={showConversation ? 'rgba(59,130,246,0.9)' : 'rgba(59,130,246,0.7)'}
                  speed="12s"
                  thickness={2}
                  className="rounded-2xl"
                >
                <div className="relative w-full">
                <Sparkles className={`absolute left-4 md:left-6 top-1/2 -translate-y-1/2 w-5 h-5 md:w-6 md:h-6 z-10 transition-colors ${
                  hasConversationMessages && !showConversation ? 'text-blue-300' : 'text-blue-400'
                }`} />
                
                <div 
                  className={`w-full h-14 md:h-20 pl-12 md:pl-16 pr-4 md:pr-5 bg-[#09090b] backdrop-blur-2xl transition-all duration-200 flex items-center rounded-2xl ${
                    showConversation 
                      ? 'shadow-lg shadow-blue-500/5' 
                      : ''
                  }`}
                >
                  {!showConversation ? (
                    <>
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => {
                          setConversationInitialMessage(null);
                          setShowConversation(true);
                          setIsExploringMode(true);
                        }}
                        placeholder={hasConversationMessages ? "Continue your conversation..." : "Ask me anything... What are you building?"}
                        className="flex-1 h-full bg-transparent focus:outline-none text-white text-base md:text-xl placeholder-white/40 pr-12 md:pr-16"
                      />
                      {hasConversationMessages && (
                        <div className="absolute right-20 top-1/2 -translate-y-1/2 flex items-center gap-2.5">
                          <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
                          <span className="text-sm text-blue-400/70 tracking-wide">Active</span>
                        </div>
                      )}
                      <button 
                        onClick={() => {
                          setShowConversation(true);
                          setIsExploringMode(true);
                        }}
                        className="group absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full z-10 bg-white/[0.06] border border-white/[0.08] hover:bg-blue-500/15 hover:border-blue-400/30 active:scale-95 transition-all"
                      >
                        <ArrowRight className="w-4 h-4 text-zinc-400 group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all" />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-white/60 text-lg">Touchstone Guide</span>
                      <button 
                        onClick={() => {
                          setShowConversation(false);
                          setHighlightedToolNames([]);
                          setDimmedToolNames([]);
                          setSearchQuery('');
                        }}
                        className="p-2 hover:bg-zinc-800 rounded-lg text-zinc-500 hover:text-white transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </>
                  )}
                </div>
                </div>
                </StarBorder>
                
                {/* Dropdown Conversation */}
                <ConversationOverlay
                  isOpen={showConversation}
                  onClose={() => {
                    setShowConversation(false);
                    setHighlightedToolNames([]);
                    setDimmedToolNames([]);
                    setSearchQuery('');
                  }}
                  onHighlightTools={setHighlightedToolNames}
                  onDimTools={setDimmedToolNames}
                  onProfileUpdate={(updates) => {
                    if (updates.role) setUserRole(updates.role);
                    if (updates.task) setActiveTask(updates.task);
                    if (updates.budget) {
                      const budgetMap = { 'free': '', 'low': '50', 'medium': '100', 'high': '500' };
                      setPricingPrefs(prev => ({ ...prev, monthlyBudget: budgetMap[updates.budget] || '' }));
                    }
                    if (updates.priorities) {
                      setPricingPrefs(prev => ({ ...prev, priorities: { ...prev.priorities, ...updates.priorities } }));
                    }
                  }}
                  userProfile={authProfile || savedProfileRef.current}
                  initialMessage={conversationInitialMessage}
                  isDropdown={true}
                  externalInput={searchQuery}
                  onExternalInputClear={() => setSearchQuery('')}
                  toolData={toolData}
                  onAddToStack={toggleStack}
                  onSelectTool={setSelectedTool}
                  onHasMessages={setHasConversationMessages}
                />
              </>
            )}
          </div>

          {/* Goal Action Buttons */}
          <div className={`w-full ${isExploring ? 'mt-4' : 'mt-8'}`}>
            <CategoryFilterBar
              activeId={activeGoal}
              onAction={handleGoalAction}
            />
          </div>
        </div>
      </div>

      {/* Zoom Controls */}
      <div className={`absolute bottom-4 right-3 md:bottom-6 md:left-6 md:right-auto flex flex-col items-center gap-1.5 z-50 ${isDragging ? 'pointer-events-none' : ''}`} style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} role="group" aria-label="Zoom controls">
        <span className="text-white/50 text-[10px] md:text-xs font-mono mb-0.5">{Math.round(((zoomLevel - 0.5) / (1.3 - 0.5)) * 100)}%</span>
        <button 
          onClick={() => setZoomLevel(z => Math.min(z + 0.15, 1.3))} 
          className="w-10 h-10 md:w-9 md:h-9 bg-black/40 md:bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.12] rounded-xl flex items-center justify-center text-white/70 hover:text-white transition-all backdrop-blur-md"
          aria-label="Zoom in"
        >
          <Plus className="w-4 h-4" />
        </button>
        <button 
          onClick={() => setZoomLevel(z => Math.max(z - 0.15, 0.5))} 
          className="w-10 h-10 md:w-9 md:h-9 bg-black/40 md:bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.12] rounded-xl flex items-center justify-center text-white/70 hover:text-white transition-all backdrop-blur-md"
          aria-label="Zoom out"
        >
          <Minus className="w-4 h-4" />
        </button>
      </div>

      {/* Stack */}
      {currentStack.length > 0 && (
        <div className={`absolute bottom-6 left-1/2 -translate-x-1/2 z-40 ${isDragging ? 'pointer-events-none' : ''}`}>
          <div className="flex items-center gap-2 px-3 py-2 bg-black/50 backdrop-blur-xl border border-white/10 rounded-2xl">
            {currentStack.map(tool => (
              <div key={tool.id} className="relative group">
                <div className="cursor-pointer hover:scale-110 transition-transform" onClick={() => setSelectedTool(tool)}>
                  <ToolLogo tool={tool} size="sm" />
                </div>
                <button 
                  onClick={() => toggleStack(tool)} 
                  className="absolute -top-1 -right-1 w-4 h-4 bg-zinc-900 border border-zinc-700 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label={`Remove ${tool.name} from stack`}
                >
                  <X className="w-2.5 h-2.5 text-white/60" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Detail Panel - Right Side */}
      <div className={`absolute right-0 top-[120px] bottom-0 w-full md:w-80 z-50 transition-transform duration-300 ease-out ${selectedTool ? 'translate-x-0' : 'translate-x-full'} ${isDragging ? 'pointer-events-none' : ''}`}>
        {selectedTool && (
          <div className="h-full bg-zinc-900/98 backdrop-blur-xl border-l border-zinc-700/50 shadow-2xl overflow-y-auto rounded-tl-2xl">
            <div className="sticky top-0 bg-zinc-900/98 backdrop-blur-xl z-10 px-4 py-3 border-b border-zinc-800">
              <button onClick={() => setSelectedTool(null)} className="absolute top-3 right-3 text-zinc-500 hover:text-zinc-300 p-1 hover:bg-zinc-800 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
              
              <div className="flex items-center gap-3 pr-8">
                <ToolLogo tool={selectedTool} size="lg" className="shadow-lg" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-lg font-medium text-white">{selectedTool.name}</h2>
                    {selectedFitScore && (
                      <FitScoreBadge score={selectedFitScore} onClick={() => setShowFitDetails(!showFitDetails)} expanded={showFitDetails} />
                    )}
                  </div>
                  <p className="text-xs text-zinc-500">{selectedTool.category} • {selectedTool.provider}</p>
                </div>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {/* Tool Info */}
              <div>
                  {activeTask && selectedFitLabel && (
                    <div className={`mb-3 px-3 py-2 rounded-lg ${selectedFitLabel.bg} border border-zinc-800/40`}>
                      <div className="flex items-center gap-2">
                        <Target className="w-3.5 h-3.5 text-zinc-400" />
                        <span className={`text-sm font-medium ${selectedFitLabel.color}`}>{selectedFitLabel.label}</span>
                        <span className="text-xs text-zinc-600">for {taskDef?.label}</span>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-zinc-400 mb-3 line-clamp-2">{selectedTool.guidance}</p>
                  
                  <button 
                    onClick={() => toggleStack(selectedTool)}
                    className={`w-full py-2 rounded-lg text-xs font-medium transition-all ${
                      inStack(selectedTool.id) ? 'bg-indigo-500 text-white' : 'bg-emerald-500 text-white hover:bg-emerald-600'
                    }`}
                  >
                    {inStack(selectedTool.id) ? 'Remove from Stack' : 'Add to Stack'}
                  </button>
              </div>

              {/* Capabilities */}
              <div className="border-t border-zinc-800 pt-4">
                  {(() => {
                    const taskCategories = taskDef?.categories || [];
                    const toolMatchesTask = !activeTask || taskCategories.includes(selectedTool.category);
                    
                    if (activeTask && !toolMatchesTask) {
                      return (
                        <div className="flex-1">
                          <div className="text-xs text-zinc-500 mb-3 font-medium uppercase tracking-wider">
                            Why {selectedFitScore}% fit for {taskDef?.label}
                          </div>
                          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg mb-3">
                            <div className="flex items-center gap-2 text-red-400 text-sm font-medium mb-1">
                              <span>⚠</span>
                              <span>Category Mismatch</span>
                            </div>
                            <p className="text-xs text-zinc-400">
                              {selectedTool.name} is a <span className="text-white">{selectedTool.category}</span> tool.
                              This task requires: <span className="text-white">{taskCategories.join(', ')}</span>.
                            </p>
                          </div>
                          <div className="text-xs text-zinc-600 mb-2">Tool's actual strengths:</div>
                          <div className="space-y-1 opacity-60">
                            {['Output Quality', 'Accuracy', 'Speed', 'Cost Efficiency'].map(dim => {
                              const level = profile?.[dim] || 'limited';
                              return <CapabilityBar key={dim} label={dim} level={level} emphasized={false} />;
                            })}
                          </div>
                        </div>
                      );
                    } else if (activeTask && taskDef?.requirements) {
                      return (
                        <div className="flex-1">
                          <div className="text-xs text-zinc-500 mb-3 font-medium uppercase tracking-wider">
                            Requirements for {taskDef?.label}
                          </div>
                          <div className="space-y-0.5">
                            {Object.entries(taskDef.requirements).map(([dim, req]) => {
                              const level = profile?.[dim] || 'limited';
                              return <CapabilityBar key={dim} label={dim} level={level} emphasized={true} requirement={req.min} />;
                            })}
                          </div>
                        </div>
                      );
                    } else {
                      return (
                        <div>
                          <div className="text-xs text-zinc-500 mb-2 font-medium uppercase tracking-wider">Key Capabilities</div>
                          <div className="space-y-0.5">
                            {['Output Quality', 'Accuracy', 'Speed', 'Cost Efficiency', 'Integration Ease', 'API Robustness'].map(dim => {
                              const level = profile?.[dim] || 'limited';
                              return <CapabilityBar key={dim} label={dim} level={level} emphasized={false} />;
                            })}
                          </div>
                        </div>
                      );
                    }
                  })()}
              </div>

              {/* Integrations */}
              <div className="border-t border-zinc-800 pt-4">
                  {selectedTool.compatible?.length > 0 && (
                    <div className="mb-3">
                      <div className="text-[10px] text-zinc-500 mb-1.5 uppercase tracking-wider">AI Tools</div>
                      <div className="flex flex-wrap gap-1">
                        {selectedTool.compatible.slice(0, 8).map(id => {
                          const t = toolData.find(x => x.id === id);
                          return t ? (
                            <button key={id} onClick={() => setSelectedTool(t)} className="hover:scale-110 transition-transform" title={t.name}>
                              <ToolLogo tool={t} size="xs" />
                            </button>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                  
                  {selectedTool.software?.length > 0 && (
                    <div>
                      <div className="text-[10px] text-zinc-500 mb-1.5 uppercase tracking-wider">Software</div>
                      <div className="flex flex-wrap gap-1">
                        {selectedTool.software.slice(0, 5).map(sw => (
                          <span key={sw} className="px-1.5 py-0.5 bg-zinc-800/50 text-zinc-500 text-[9px] rounded">{sw}</span>
                        ))}
                      </div>
                    </div>
                  )}
              </div>

              {/* Pricing & Actions */}
              <div className="border-t border-zinc-800 pt-4">
                  <div className="space-y-2">
                    {selectedTool.pricing && (
                      <div>
                        <div className="flex items-center gap-1 mb-1">
                          <DollarSign className="w-3 h-3 text-emerald-500" />
                          <span className="text-xs text-zinc-500 font-medium">Pricing</span>
                        </div>
                        <div className="text-sm text-white">{selectedTool.pricing.cost}</div>
                        {selectedTool.pricing.free && (
                          <span className="inline-block mt-1 px-1.5 py-0.5 bg-emerald-500/15 text-emerald-400 text-[10px] rounded">Free tier</span>
                        )}
                      </div>
                    )}
                    
                    {selectedTool.constraints && (
                      <div>
                        <div className="flex items-center gap-1 mb-1">
                          <Shield className="w-3 h-3 text-zinc-500" />
                          <span className="text-xs text-zinc-500 font-medium">Compliance</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {selectedTool.constraints.soc2 && <span className="px-1.5 py-0.5 bg-emerald-500/15 text-emerald-400 text-[10px] rounded">SOC2</span>}
                          {selectedTool.constraints.hipaa && <span className="px-1.5 py-0.5 bg-emerald-500/15 text-emerald-400 text-[10px] rounded">HIPAA</span>}
                          {selectedTool.constraints.onprem && <span className="px-1.5 py-0.5 bg-teal-500/15 text-teal-400 text-[10px] rounded">Self-host</span>}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-1.5 mt-3 pt-2 border-t border-zinc-800">
                    <button 
                      onClick={() => {
                        if (showCategoryCompare) setShowCategoryCompare(false);
                        else { setShowCategoryCompare(true); setShowCompareMenu(false); setCompareTarget(null); }
                      }}
                      className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[10px] transition-colors ${
                        showCategoryCompare ? 'bg-teal-500/20 border border-teal-500/30 text-teal-300' : 'bg-zinc-800/50 border border-zinc-700/50 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
                      }`}
                    >
                      <BarChart3 className="w-3 h-3" />
                      <span>Compare category</span>
                    </button>
                    
                    <button 
                      onClick={() => {
                        if (showCompareMenu || compareTarget) { setShowCompareMenu(false); setCompareTarget(null); }
                        else { setShowCompareMenu(true); setShowCategoryCompare(false); }
                      }}
                      className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[10px] transition-colors ${
                        showCompareMenu || compareTarget ? 'bg-teal-500/20 border border-teal-500/30 text-teal-300' : 'bg-zinc-800/50 border border-zinc-700/50 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
                      }`}
                    >
                      <GitCompare className="w-3 h-3" />
                      <span>Compare 1-on-1</span>
                    </button>
                  </div>
              </div>
              
              {/* Fit Score Breakdown */}
              {showFitDetails && selectedFitBreakdown && (
                <div className="border-t border-zinc-800 pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="relative w-10 h-10">
                        <svg className="w-10 h-10 -rotate-90">
                          <circle cx="20" cy="20" r="16" fill="none" stroke="#27272a" strokeWidth="3" />
                          <circle 
                            cx="20" cy="20" r="16" fill="none" 
                            stroke={selectedFitScore >= 85 ? '#10b981' : selectedFitScore >= 70 ? '#14b8a6' : selectedFitScore >= 55 ? '#f59e0b' : '#ef4444'}
                            strokeWidth="3" 
                            strokeLinecap="round"
                            strokeDasharray={`${(selectedFitScore / 100) * 100} 100`}
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs font-bold text-white">{selectedFitScore}</span>
                        </div>
                      </div>
                      <div className={`text-xs font-medium ${
                        selectedFitScore >= 85 ? 'text-emerald-400' : selectedFitScore >= 70 ? 'text-teal-400' : selectedFitScore >= 55 ? 'text-amber-400' : 'text-red-400'
                      }`}>
                        {selectedFitScore >= 85 ? 'Excellent' : selectedFitScore >= 70 ? 'Good' : selectedFitScore >= 55 ? 'Viable' : 'Poor'}
                      </div>
                    </div>
                    <button onClick={() => setShowFitDetails(false)} className="p-1 hover:bg-zinc-800 rounded text-zinc-500 hover:text-zinc-300">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Task Requirements</div>
                    {selectedFitBreakdown.categoryMatch ? (
                      <div className="space-y-1">
                        {selectedFitBreakdown.capabilityScores?.map(cap => (
                          <div key={cap.dimension} className="flex items-center gap-2">
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                              cap.status === 'exceeds' ? 'bg-emerald-400' : cap.status === 'meets' ? 'bg-teal-400' : 'bg-red-400'
                            }`} />
                            <span className="text-[11px] text-zinc-300 flex-1">{cap.dimension}</span>
                            <span className={`text-[10px] font-medium ${
                              cap.status === 'exceeds' ? 'text-emerald-400' : cap.status === 'meets' ? 'text-teal-400' : 'text-red-400'
                            }`}>
                              {cap.status === 'exceeds' ? 'Exceeds' : cap.status === 'meets' ? 'Meets' : 'Below'}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-red-400">
                        <span className="text-xs">⚠</span>
                        <span className="text-xs">Category mismatch</span>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Modifiers</div>
                    <div className="flex flex-wrap gap-1">
                      {userRole && selectedFitBreakdown.roleBonus !== 0 && (
                        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] ${
                          selectedFitBreakdown.roleBonus > 0 ? 'bg-violet-500/20 text-violet-300' : 'bg-red-500/20 text-red-300'
                        }`}>
                          {userRoles.find(r => r.id === userRole)?.icon} {selectedFitBreakdown.roleBonus > 0 ? '+' : ''}{selectedFitBreakdown.roleBonus}%
                        </span>
                      )}
                      {selectedFitBreakdown.softwareBonus > 0 && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-teal-500/20 text-teal-300">
                          🔗+{selectedFitBreakdown.softwareBonus}%
                        </span>
                      )}
                      {!userRole && !selectedFitBreakdown.softwareBonus && (
                        <span className="text-[10px] text-zinc-600">Base score</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Compare Modal */}
      {selectedTool && (showCompareMenu || compareTarget || showCategoryCompare) && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-8">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => { setShowCompareMenu(false); setShowCategoryCompare(false); setCompareTarget(null); }} />
          
          <div className="relative bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <button onClick={() => { setShowCompareMenu(false); setShowCategoryCompare(false); setCompareTarget(null); }} className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-300 p-1.5 hover:bg-zinc-800 rounded-lg transition-colors z-10">
              <X className="w-5 h-5" />
            </button>

            {showCompareMenu && !compareTarget && (
              <div className="p-6">
                <div className="text-lg text-white font-medium mb-1">Compare 1-on-1</div>
                <div className="text-sm text-zinc-500 mb-5">Select a tool to compare with {selectedTool.name}</div>
                <div className="grid grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                  {toolData
                    .filter(t => t.id !== selectedTool.id)
                    .map(t => {
                      const result = activeTask ? calculateTaskFitScore(t, activeTask, filters, userSoftware) : { score: 50 };
                      return { ...t, fitScore: result.score };
                    })
                    .sort((a, b) => b.fitScore - a.fitScore)
                    .map(t => {
                      const fit = getTaskFitLabel(t.fitScore);
                      return (
                        <button key={t.id} onClick={() => { setCompareTarget(t); setShowCompareMenu(false); }} className="flex items-center gap-3 p-3 bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-zinc-600 rounded-xl transition-all text-left">
                          <ToolLogo tool={t} size="md" className="flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-zinc-200 font-medium truncate">{t.name}</div>
                            <div className="text-xs text-zinc-600">{t.category}</div>
                            <div className={`text-xs ${fit.color}`}>{fit.label} · {t.fitScore}%</div>
                          </div>
                        </button>
                      );
                    })}
                </div>
              </div>
            )}

            {compareTarget && (
              <div className="p-6">
                <ComparisonView toolA={selectedTool} toolB={compareTarget} onClose={() => setCompareTarget(null)} activeTask={activeTask} userSoftware={userSoftware} />
              </div>
            )}

            {showCategoryCompare && (
              <div className="p-6">
                <CategoryCompareView 
                  category={selectedTool.category} 
                  referenceTool={selectedTool} 
                  onClose={() => setShowCategoryCompare(false)}
                  onSelectTool={(tool) => { setSelectedTool(tool); setShowCategoryCompare(false); }}
                  activeTask={activeTask}
                />
              </div>
            )}
          </div>
        </div>
      )}
        </>
      ) : currentPage === 'onboarding' ? (
        /* Onboarding quiz — full screen below header */
        <div className="absolute inset-0 z-0">
          <Suspense fallback={<PageLoader label="Loading onboarding..." />}>
            <OnboardingQuiz
              initialStep={location.state?.editStep}
              onComplete={async (profile) => {
                await saveProfile(profile);
                savedProfileRef.current = profile;
                navigate('/');
              }}
              onSkip={() => navigate('/')}
            />
          </Suspense>
        </div>
      ) : currentPage === 'home' ? (
        /* Homepage — z-0 so header (z-[60]) stays on top */
        <div className="absolute inset-0 z-0 overflow-y-auto">
          <Suspense fallback={<PageLoader label="Loading homepage..." />}>
            <HomePage />
          </Suspense>
          <Footer />
        </div>
      ) : currentPage === 'workflow' ? (
        /* Workflow Page — footer below the fold */
        <div className="absolute inset-0 overflow-y-auto overflow-x-hidden">
          <Suspense fallback={<PageLoader label="Loading workflow..." />}>
            <WorkflowPage 
              userProfile={{
                role: userRole,
                software: userSoftware,
                priorities: pricingPrefs.priorities,
                budget: pricingPrefs.monthlyBudget,
              }}
            />
          </Suspense>
          {!checkOnboarded() && (
            <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-md">
              <div className="max-w-sm w-full mx-4">
                <SignInPrompt
                  mode="modal"
                  onClose={() => handlePageChange('explore')}
                  onSignedIn={() => navigate('/onboarding')}
                />
              </div>
            </div>
          )}
          <Footer />
        </div>
      ) : currentPage === 'database' ? (
        /* Database — list or tool detail (pricing calculator hidden — UNDER CONSTRUCTION) */
        <Suspense fallback={<PageLoader label="Loading database..." />}>
          {location.pathname.match(/^\/database\/[^/]+/) ? <ToolDetailPage /> : <DatabasePage />}
        </Suspense>
      ) : (
        /* Blog Page */
        <div className="absolute inset-0 overflow-y-auto">
          <Suspense fallback={<PageLoader label="Loading blog..." />}>
            <BlogPage />
          </Suspense>
          <Footer />
        </div>
      )}

      {/* Profile Panel */}
      <StaggeredProfileMenu
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        colors={['#0c0c0e', '#111113']}
        accentColor="#14b8a6"
      >
        {(() => {
          const p = savedProfileRef.current || {};
          const techLevelMap = { 1: 'Non-Technical', 2: 'Basic', 3: 'Intermediate', 4: 'Advanced', 5: 'Expert' };
          const industryMap = {
            marketing: 'Marketing & Advertising', engineering: 'Engineering & Dev',
            sales: 'Sales & Revenue', creative: 'Creative & Design',
            enterprise: 'Enterprise & Ops', research: 'Research & Academia',
            healthcare: 'Healthcare & Biotech', finance: 'Finance & Fintech',
            education: 'Education & Training', other: 'Other',
          };
          const teamMap = { solo: 'Solo', '2-5': '2–5', '6-20': '6–20', '21-100': '21–100', '100+': '100+' };
          const stageMap = { personal: 'Personal Use', 'side-project': 'Side Project', 'early-startup': 'Early Startup', growing: 'Growing Business', established: 'Established Company', enterprise: 'Enterprise' };
          const budgetMap = { free: { l: 'Free Only', d: '$0/mo' }, low: { l: 'Starter', d: '<$50/mo' }, medium: { l: 'Growth', d: '$50–200/mo' }, high: { l: 'Scale', d: '$200–1k/mo' }, enterprise: { l: 'Enterprise', d: '$1000+/mo' } };
          const prioMap = { cost: 'Cost Efficiency', quality: 'Output Quality', speed: 'Speed', easeOfUse: 'Ease of Use', support: 'Support & Docs' };
          const reqLabels = { requireFreeTier: 'Free Tier', preferOpenSource: 'Open Source', requireSelfHost: 'Self-Hostable', requireSOC2: 'SOC 2', requireHIPAA: 'HIPAA', requireGDPR: 'GDPR' };

          const editStep = (stepIdx) => {
            setShowProfileModal(false);
            navigate('/onboarding', { state: { editStep: stepIdx } });
          };

          const EditBtn = ({ step }) => (
            <button onClick={() => editStep(step)} className="text-sm text-zinc-600 hover:text-teal-400 px-3 py-1.5 rounded-lg hover:bg-white/[0.03] transition-all border border-transparent hover:border-zinc-800">
              Edit
            </button>
          );

          const tabs = [
            { id: 'overview', label: 'Overview' },
            { id: 'work', label: 'Work' },
            { id: 'preferences', label: 'Preferences' },
            { id: 'stack', label: 'Stack' },
          ];

          const bgt = budgetMap[p.budget];
          const activeReqs = Object.entries(p.requirements || {}).filter(([, v]) => v);

          const Row = ({ label, value, step }) => (
            <div className="flex items-center justify-between py-3.5">
              <div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider">{label}</div>
                <div className="text-base text-zinc-200 mt-1">{value || <span className="text-zinc-700 italic text-sm">Not set</span>}</div>
              </div>
              {step != null && <EditBtn step={step} />}
            </div>
          );

          const Chip = ({ children }) => (
            <span className="px-3.5 py-1.5 rounded-lg bg-white/[0.04] border border-zinc-800/60 text-sm text-zinc-300">{children}</span>
          );

          return (
            <>
              {/* Header */}
              <div className="stagger-item sticky top-0 bg-[#0c0c0e]/95 backdrop-blur-xl z-10 border-b border-white/[0.04]">
                <div className="px-5 py-5">
                  <button onClick={() => setShowProfileModal(false)} className="absolute top-4 right-4 text-zinc-600 hover:text-zinc-300 p-1.5 hover:bg-white/[0.04] rounded-lg transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                  <div className="flex items-center gap-4 pr-8">
                    <div className="w-12 h-12 rounded-full bg-zinc-800 border border-zinc-700/50 flex items-center justify-center text-zinc-400 font-bold text-lg shrink-0">
                      {(user?.displayName?.[0] || user?.email?.[0] || '?').toUpperCase()}
                    </div>
                    <div>
                      <div className="text-lg text-zinc-100 font-semibold">{user?.displayName || 'Profile'}</div>
                      <div className="text-sm text-zinc-500">{user?.email || ''}</div>
                    </div>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex px-4 gap-1">
                  {tabs.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setProfileTab(tab.id)}
                      className={`px-4 py-3 text-sm font-medium transition-all border-b-2 ${
                        profileTab === tab.id
                          ? 'text-zinc-100 border-teal-500'
                          : 'text-zinc-600 border-transparent hover:text-zinc-400'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab content */}
              <div className="p-5 pb-8">

                {/* OVERVIEW */}
                {profileTab === 'overview' && (
                  <div className="stagger-item space-y-5">
                    <div className="rounded-xl bg-white/[0.02] border border-white/[0.04] p-5 divide-y divide-white/[0.04]">
                      <Row label="Website" step={1} value={
                        p.websiteUrl ? (
                          <a href={p.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-teal-500 hover:text-teal-400 text-base">
                            {p.websiteUrl.replace(/^https?:\/\//, '')}
                          </a>
                        ) : null
                      } />
                      <Row label="Tech Level" step={2} value={techLevelMap[p.techLevel]} />
                      <Row label="Team Size" step={5} value={teamMap[p.teamSize]} />
                      <Row label="Company Stage" step={6} value={stageMap[p.companyStage]} />
                    </div>

                    <div className="rounded-xl bg-white/[0.02] border border-white/[0.04] p-5">
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-xs text-zinc-500 uppercase tracking-wider font-medium">Quick Summary</div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-lg bg-white/[0.02] border border-white/[0.04] px-4 py-4">
                          <div className="text-xs text-zinc-500 uppercase">Industries</div>
                          <div className="text-xl text-zinc-200 font-semibold mt-1">{p.industry?.length || 0}</div>
                          <div className="text-xs text-zinc-600 mt-0.5">selected</div>
                        </div>
                        <div className="rounded-lg bg-white/[0.02] border border-white/[0.04] px-4 py-4">
                          <div className="text-xs text-zinc-500 uppercase">Roles</div>
                          <div className="text-xl text-zinc-200 font-semibold mt-1">{p.role?.length || 0}</div>
                          <div className="text-xs text-zinc-600 mt-0.5">selected</div>
                        </div>
                        <div className="rounded-lg bg-white/[0.02] border border-white/[0.04] px-4 py-4">
                          <div className="text-xs text-zinc-500 uppercase">Budget</div>
                          <div className="text-xl text-zinc-200 font-semibold mt-1">{bgt?.d || '—'}</div>
                          <div className="text-xs text-zinc-600 mt-0.5">{bgt?.l || 'Not set'}</div>
                        </div>
                        <div className="rounded-lg bg-white/[0.02] border border-white/[0.04] px-4 py-4">
                          <div className="text-xs text-zinc-500 uppercase">Tools</div>
                          <div className="text-xl text-zinc-200 font-semibold mt-1">{p.software?.length || 0}</div>
                          <div className="text-xs text-zinc-600 mt-0.5">in stack</div>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => editStep(0)}
                      className="w-full py-3 rounded-xl border border-white/[0.05] text-sm text-zinc-600 hover:text-zinc-300 hover:border-zinc-700 transition-colors"
                    >
                      Redo full onboarding
                    </button>
                  </div>
                )}

                {/* WORK */}
                {profileTab === 'work' && (
                  <div className="stagger-item space-y-5">
                    <div className="rounded-xl bg-white/[0.02] border border-white/[0.04] p-5">
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-sm text-zinc-400 font-medium">Industries</div>
                        <EditBtn step={3} />
                      </div>
                      {p.industry?.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {p.industry.map(id => <Chip key={id}>{industryMap[id] || id}</Chip>)}
                        </div>
                      ) : (
                        <button onClick={() => editStep(3)} className="w-full py-5 rounded-lg border border-dashed border-zinc-800/60 text-sm text-zinc-700 hover:text-teal-500 hover:border-teal-500/30 transition-all">
                          + Add industries
                        </button>
                      )}
                    </div>

                    <div className="rounded-xl bg-white/[0.02] border border-white/[0.04] p-5">
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-sm text-zinc-400 font-medium">Roles</div>
                        <EditBtn step={4} />
                      </div>
                      {p.role?.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {p.role.map(id => {
                            const r = userRoles.find(x => x.id === id);
                            return <Chip key={id}>{r?.name || id}</Chip>;
                          })}
                        </div>
                      ) : (
                        <button onClick={() => editStep(4)} className="w-full py-5 rounded-lg border border-dashed border-zinc-800/60 text-sm text-zinc-700 hover:text-teal-500 hover:border-teal-500/30 transition-all">
                          + Add roles
                        </button>
                      )}
                    </div>

                    <div className="rounded-xl bg-white/[0.02] border border-white/[0.04] p-5">
                      <div className="text-sm text-zinc-400 font-medium mb-2">Team & Company</div>
                      <div className="divide-y divide-white/[0.04]">
                        <Row label="Team Size" step={5} value={teamMap[p.teamSize]} />
                        <Row label="Company Stage" step={6} value={stageMap[p.companyStage]} />
                      </div>
                    </div>
                  </div>
                )}

                {/* PREFERENCES */}
                {profileTab === 'preferences' && (
                  <div className="stagger-item space-y-5">
                    <div className="rounded-xl bg-white/[0.02] border border-white/[0.04] p-5">
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-sm text-zinc-400 font-medium">Priority Ranking</div>
                        <EditBtn step={7} />
                      </div>
                      {p.priorities?.ranked?.length > 0 ? (
                        <div className="space-y-2">
                          {p.priorities.ranked.map((key, i) => (
                            <div key={key} className="flex items-center gap-4 py-2">
                              <span className="text-base font-mono text-zinc-500 w-5 text-right">{i + 1}</span>
                              <div className="flex-1 h-px bg-white/[0.04]" />
                              <span className="text-base text-zinc-300">{prioMap[key] || key}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <button onClick={() => editStep(7)} className="w-full py-5 rounded-lg border border-dashed border-zinc-800/60 text-sm text-zinc-700 hover:text-teal-500 hover:border-teal-500/30 transition-all">
                          + Set priorities
                        </button>
                      )}
                    </div>

                    <div className="rounded-xl bg-white/[0.02] border border-white/[0.04] p-5">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm text-zinc-400 font-medium">Budget</div>
                        <EditBtn step={8} />
                      </div>
                      {bgt ? (
                        <div className="mt-2">
                          <div className="text-2xl text-zinc-100 font-bold">{bgt.d}</div>
                          <div className="text-sm text-zinc-500 mt-1">{bgt.l} tier</div>
                        </div>
                      ) : (
                        <button onClick={() => editStep(8)} className="w-full py-5 mt-2 rounded-lg border border-dashed border-zinc-800/60 text-sm text-zinc-700 hover:text-teal-500 hover:border-teal-500/30 transition-all">
                          + Set budget
                        </button>
                      )}
                    </div>

                    <div className="rounded-xl bg-white/[0.02] border border-white/[0.04] p-5">
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-sm text-zinc-400 font-medium">Requirements</div>
                        <EditBtn step={10} />
                      </div>
                      {activeReqs.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {activeReqs.map(([key]) => (
                            <span key={key} className="px-3.5 py-1.5 rounded-lg bg-teal-500/[0.06] border border-teal-500/15 text-sm text-teal-400">{reqLabels[key] || key}</span>
                          ))}
                        </div>
                      ) : <div className="text-sm text-zinc-700">No hard requirements</div>}
                    </div>
                  </div>
                )}

                {/* STACK */}
                {profileTab === 'stack' && (
                  <div className="stagger-item space-y-5">
                    <div className="rounded-xl bg-white/[0.02] border border-white/[0.04] p-5">
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-sm text-zinc-400 font-medium">Your Tools ({p.software?.length || 0})</div>
                        <EditBtn step={9} />
                      </div>
                      {p.software?.length > 0 ? (
                        <div className="space-y-4">
                          {(() => {
                            const cats = {};
                            (p.software || []).forEach(id => {
                              const sw = availableSoftware.find(s => s.id === id);
                              if (!sw) return;
                              if (!cats[sw.category]) cats[sw.category] = [];
                              cats[sw.category].push(sw);
                            });
                            return Object.entries(cats).map(([cat, items]) => (
                              <div key={cat}>
                                <div className="text-xs text-zinc-600 uppercase tracking-wider mb-2">{cat}</div>
                                <div className="flex flex-wrap gap-2">
                                  {items.map(sw => <Chip key={sw.id}>{sw.name}</Chip>)}
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      ) : (
                        <button onClick={() => editStep(9)} className="w-full py-8 rounded-lg border border-dashed border-zinc-800/60 text-sm text-zinc-700 hover:text-teal-500 hover:border-teal-500/30 transition-all">
                          + Add your software stack
                        </button>
                      )}
                    </div>

                    <div className="rounded-xl bg-white/[0.02] border border-white/[0.04] p-5">
                      <Row label="Website" step={1} value={
                        p.websiteUrl ? (
                          <a href={p.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-teal-500 hover:text-teal-400 text-base">
                            {p.websiteUrl.replace(/^https?:\/\//, '')}
                          </a>
                        ) : null
                      } />
                    </div>
                  </div>
                )}

              </div>
            </>
          );
        })()}
      </StaggeredProfileMenu>

    </div>
    </ConversationProvider>
  );
}

